'use client';
/**
 * The one destructive-confirmation shape the app uses (delete album, delete photo, remove spread).
 * Everything visual comes from `@freealbum/ui`; this only fixes the button order and the busy state so
 * every "are you sure?" in freeAlbum behaves the same way.
 *
 * The confirmation also owns the *outcome* (docs/10-ux-audit.md · UX-30). It used to close as soon as
 * `onConfirm` resolved, and callers resolved even after a failed request, so a deletion that never
 * happened looked exactly like one that did. A handler now either succeeds, returns `false`, or throws:
 * the last two keep the dialog open with an inline explanation and a Retry button, so the reader keeps
 * the context of what they were confirming.
 */
import * as React from 'react';
import { useTranslations } from 'next-intl';
import { AlertTriangle } from 'lucide-react';
import { Button, Dialog } from '@freealbum/ui';
import { useApiErrorMessage } from '@/hooks/use-api-error';

/** `false` means "it did not happen" — the dialog stays open. `void`/`true` means done. */
export type ConfirmOutcome = void | boolean;

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: React.ReactNode;
  description?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  /**
   * Awaited before the dialog closes, so the button can show a spinner.
   * Return `false` — or let the error propagate — to keep the dialog open with a retry.
   */
  onConfirm: () => ConfirmOutcome | Promise<ConfirmOutcome>;
  /** Turns a thrown value into the inline message. Defaults to the shared API error formatter. */
  onError?: (err: unknown) => string;
  children?: React.ReactNode;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  cancelLabel,
  destructive = true,
  onConfirm,
  onError,
  children,
}: ConfirmDialogProps) {
  const t = useTranslations('common');
  const apiErrorMessage = useApiErrorMessage();
  const [busy, setBusy] = React.useState(false);
  const [failure, setFailure] = React.useState<string | null>(null);
  // Two presses in the same tick share one render closure, so `busy` would still read false in the
  // second one. The ref is the only guard that is already true when the second handler runs.
  const running = React.useRef(false);

  // A reopened dialog must not inherit the previous attempt's failure.
  React.useEffect(() => {
    if (open) setFailure(null);
  }, [open]);

  const confirm = async () => {
    // A second activation while the first request is in flight must not send another one.
    if (running.current) return;
    running.current = true;
    setBusy(true);
    setFailure(null);
    try {
      const outcome = await onConfirm();
      if (outcome === false) {
        setFailure(t('actionFailed'));
        return;
      }
      onOpenChange(false);
    } catch (err) {
      setFailure(onError ? onError(err) : apiErrorMessage(err));
    } finally {
      running.current = false;
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!busy) onOpenChange(next);
      }}
      title={title}
      description={description}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
            {cancelLabel ?? t('cancel')}
          </Button>
          <Button
            variant={destructive ? 'danger' : 'primary'}
            onClick={() => void confirm()}
            loading={busy}
            disabled={busy}
          >
            {failure ? t('retry') : (confirmLabel ?? t('confirm'))}
          </Button>
        </>
      }
    >
      {children}
      {failure ? (
        <p role="alert" className="mt-3 flex items-start gap-2 rounded-md bg-danger-soft px-3 py-2 text-sm text-danger">
          <AlertTriangle aria-hidden className="mt-0.5 size-4 shrink-0" />
          {failure}
        </p>
      ) : null}
    </Dialog>
  );
}
